import React, { useState } from 'react';
import { ArrowLeftRight, BadgeCheck, Package, Repeat, ShoppingBag, X } from 'lucide-react';
import { CardImage } from '../../utils/cardImageUtils';
import { formatDate } from '../../utils/locale';
import { useLanguage } from '../../i18n/LanguageContext';
import { marketplaceApi } from '../../api/marketplace';
import {
  isPeriodMarketplaceLoan,
  canCancelListedLoan,
  canUpdatePeriodLoanPrice,
  getLoanPeriodsRemaining,
  getPeriodsUsedDisplay
} from '../../utils/loanDisplayUtils';

const CardsStatusTab = ({ cardsForSale = [], soldCards = [], listedLoans = [], loanedCards = [], borrowedCards = [], loading, onCardUnlisted, user }) => {
  const ITEMS_PER_PAGE = 3;
  const { t, locale } = useLanguage();
  const [cardsForSalePage, setCardsForSalePage] = useState(0);
  const [soldPage, setSoldPage] = useState(0);
  const [listedLoansPage, setListedLoansPage] = useState(0);
  const [loanedPage, setLoanedPage] = useState(0);
  const [borrowedPage, setBorrowedPage] = useState(0);
  const [cancellingSaleId, setCancellingSaleId] = useState(null);
  const [cancellingLoanId, setCancellingLoanId] = useState(null);
  const [periodPriceEdits, setPeriodPriceEdits] = useState({});
  const [savingPeriodPriceId, setSavingPeriodPriceId] = useState(null);

  const normalizeListedLoan = (entry) => {
    const base = {
      ...entry,
      loan_kind: entry.loanKind || entry.loan_kind,
      period_count: entry.periodCount ?? entry.period_count,
      periods_used: entry.periodsUsed ?? entry.periods_used,
      price_per_period: entry.pricePerPeriod ?? entry.price_per_period,
      period_hours: entry.periodHours ?? entry.period_hours,
      status: entry.status || 'listed',
      borrower_id: entry.borrowerId ?? entry.borrower_id,
      last_period_borrower_id: entry.lastPeriodBorrowerId ?? entry.last_period_borrower_id
    };
    const displayUsed =
      entry.periodsUsedDisplay != null
        ? Number(entry.periodsUsedDisplay)
        : getPeriodsUsedDisplay(base);
    return { ...base, periodsUsedDisplay: displayUsed };
  };

  const handleCancelSale = async (saleId) => {
    if (!user?.id || !onCardUnlisted) return;
    if (!window.confirm(t('cardsStatus.confirmRemoveFromSale'))) return;
    setCancellingSaleId(saleId);
    try {
      await marketplaceApi.cancelSale(saleId, user.id);
      onCardUnlisted();
    } catch (error) {
      console.error('Error cancelling sale:', error);
      alert(error?.message || t('cardsStatus.removeFromSaleFailed'));
    } finally {
      setCancellingSaleId(null);
    }
  };

  const handleCancelLoan = async (loanId) => {
    if (!user?.id || !onCardUnlisted) return;
    if (!window.confirm(t('cardsStatus.confirmRemoveFromLoan'))) return;
    setCancellingLoanId(loanId);
    try {
      await marketplaceApi.cancelLoan(loanId, user.id);
      onCardUnlisted();
    } catch (error) {
      console.error('Error cancelling loan:', error);
      alert(error?.message || t('cardsStatus.removeFromLoanFailed'));
    } finally {
      setCancellingLoanId(null);
    }
  };

  const handleSavePeriodPrice = async (entry) => {
    const loan = normalizeListedLoan(entry);
    if (!user?.id || !canUpdatePeriodLoanPrice(loan)) return;
    const raw = periodPriceEdits[entry.id];
    const price = parseFloat(raw);
    if (!Number.isFinite(price) || price <= 0) {
      alert(t('cardsStatus.invalidPeriodPrice'));
      return;
    }
    setSavingPeriodPriceId(entry.id);
    try {
      await marketplaceApi.updatePeriodLoanPrice(entry.id, user.id, price);
      setPeriodPriceEdits((prev) => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
      onCardUnlisted();
    } catch (error) {
      alert(error?.message || t('cardsStatus.updatePeriodPriceFailed'));
    } finally {
      setSavingPeriodPriceId(null);
    }
  };

  const renderListedLoanActions = (entry) => {
    if (!onCardUnlisted || !user) return null;
    const loan = normalizeListedLoan(entry);
    const isPeriod = isPeriodMarketplaceLoan(loan);
    const canRemove = canCancelListedLoan(loan);
    const canEditPrice = canUpdatePeriodLoanPrice(loan);
    const currentPrice =
      parseFloat(loan.price_per_period) || parseFloat(entry.totalPrice) || 0;
    const editValue =
      periodPriceEdits[entry.id] !== undefined
        ? periodPriceEdits[entry.id]
        : String(currentPrice);

    return (
      <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
        {isPeriod && canEditPrice && (
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={editValue}
              onChange={(e) =>
                setPeriodPriceEdits((prev) => ({ ...prev, [entry.id]: e.target.value }))
              }
              className="w-20 px-2 py-1 text-xs rounded bg-gray-700 text-white border border-gray-600"
              aria-label={t('cardsStatus.pricePerPeriodLabel')}
            />
            <button
              type="button"
              onClick={() => handleSavePeriodPrice(entry)}
              disabled={savingPeriodPriceId === entry.id}
              className="px-2 py-1 text-xs bg-yellow-500 hover:bg-yellow-600 text-black rounded font-semibold disabled:opacity-50"
            >
              {savingPeriodPriceId === entry.id ? '...' : t('cardsStatus.savePeriodPrice')}
            </button>
          </div>
        )}
        <button
          onClick={() => handleCancelLoan(entry.id)}
          disabled={cancellingLoanId === entry.id || !canRemove}
          title={!canRemove ? t('cardsStatus.cannotRemovePeriodLoan') : undefined}
          className={`px-2 py-1 text-xs rounded flex items-center gap-1 justify-center ${
            canRemove
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          <X className="w-3 h-3" />
          {cancellingLoanId === entry.id ? '...' : t('cardsStatus.removeFromLoan')}
        </button>
      </div>
    );
  };

  const renderCard = (item, meta) => {
    const card = item.card || {};
    const displayCardId = item.sample_card_id ?? item.card_id ?? item.cardId ?? card.id;
    const playerName = card.player?.full_name || card.player?.display_name || `Card #${displayCardId}`;
    const rarityName = card.rarity?.name || 'basic';

    return (
      <div key={`${meta.keyPrefix}-${item.id}`} className="bg-gray-800 p-4 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1 sm:flex-initial">
            <div className="w-16 h-[72px] sm:w-20 sm:h-[90px] flex-shrink-0 rounded overflow-hidden bg-gray-700">
              <CardImage
                card={card}
                alt={playerName}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <p className="text-white font-semibold text-base sm:text-lg break-words leading-snug" title={playerName}>
                {playerName}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {rarityName} • {meta.label}
              </p>
              <p className="text-xs text-gray-400 font-mono">Card ID: #{displayCardId ?? t('common.notAvailable')}</p>
              <p className="text-xs text-gray-400">{meta.dateLabel}: {meta.dateValue(item)}</p>
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-end sm:flex-col sm:items-end gap-1 flex-shrink-0 sm:text-right border-t border-gray-700 pt-3 sm:border-0 sm:pt-0">
            <p className="text-yellow-400 font-semibold text-sm sm:text-base">
              {meta.amountValue(item)} DIOS
            </p>
            {meta.extraValue && (
              <p className="text-xs text-gray-400">{meta.extraValue(item)}</p>
            )}
            {meta.actionButton && meta.actionButton(item)}
          </div>
        </div>
      </div>
    );
  };

  const formatDateValue = (date) => {
    if (!date) return t('common.notAvailable');
    return formatDate(date, {}, locale);
  };

  const listedLoanAmount = (entry) => {
    const loan = normalizeListedLoan(entry);
    if (isPeriodMarketplaceLoan(loan)) {
      return (parseFloat(loan.price_per_period) || parseFloat(entry.totalPrice) || 0).toFixed(2);
    }
    return parseFloat(entry.totalPrice || 0).toFixed(2);
  };

  const listedLoanExtra = (entry) => {
    const loan = normalizeListedLoan(entry);
    if (isPeriodMarketplaceLoan(loan)) {
      const remaining = getLoanPeriodsRemaining(loan);
      const total = Number(loan.period_count) || 0;
      const used = loan.periodsUsedDisplay;
      if (loan.status === 'active') {
        return t('cardsStatus.periodActiveDetail', {
          price: (parseFloat(loan.price_per_period) || 0).toFixed(2),
          current: used,
          total,
          borrower: entry.borrowerUsername || t('common.unknown')
        });
      }
      return t('cardsStatus.periodListingDetail', {
        price: (parseFloat(loan.price_per_period) || 0).toFixed(2),
        remaining,
        total,
        used
      });
    }
    return t('cardsStatus.duration', { count: entry.durationGW });
  };

  const renderPagination = (page, setPage, totalItems) => {
    if (totalItems <= ITEMS_PER_PAGE) return null;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

    return (
      <div className="flex items-center justify-center space-x-4 mt-4">
        <button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.previous')}
        </button>
        <span className="text-gray-400 text-sm">
          {t('common.pageOf', { page: page + 1, total: totalPages })}
        </span>
        <button
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            page >= totalPages - 1
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-400 text-black hover:bg-yellow-500'
          }`}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  const paginatedCardsForSale = cardsForSale.slice(cardsForSalePage * ITEMS_PER_PAGE, (cardsForSalePage + 1) * ITEMS_PER_PAGE);
  const paginatedSold = soldCards.slice(soldPage * ITEMS_PER_PAGE, (soldPage + 1) * ITEMS_PER_PAGE);
  const paginatedListedLoans = listedLoans.slice(listedLoansPage * ITEMS_PER_PAGE, (listedLoansPage + 1) * ITEMS_PER_PAGE);
  const paginatedLoaned = loanedCards.slice(loanedPage * ITEMS_PER_PAGE, (loanedPage + 1) * ITEMS_PER_PAGE);
  const paginatedBorrowed = borrowedCards.slice(borrowedPage * ITEMS_PER_PAGE, (borrowedPage + 1) * ITEMS_PER_PAGE);

  const loanedExtra = (entry) => {
    if (entry.loanKind === 'period' || entry.loan_kind === 'period') {
      const used = Number(entry.periodsUsed ?? entry.periods_used) || 0;
      const total = Number(entry.periodCount ?? entry.period_count) || 0;
      return t('cardsStatus.periodInUse', { current: used + 1, total });
    }
    return t('cardsStatus.duration', { count: entry.durationGW });
  };

  return (
    <div className="space-y-8">
      <div className="football-card glass-football p-6">
        <div className="flex items-center gap-3 mb-4">
          <ShoppingBag className="w-6 h-6 text-yellow-400" />
          <h2 className="text-2xl font-bold text-[#1e293b]">{t('cardsStatus.cardsForSale')}</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
        ) : cardsForSale.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedCardsForSale.map((item) => renderCard(item, {
                keyPrefix: 'forsale',
                label: t('cardsStatus.forSaleLabel'),
                dateLabel: t('cardsStatus.listedDate'),
                dateValue: (entry) => formatDateValue(entry.listedAt),
                amountValue: (entry) => parseFloat(entry.price || 0).toFixed(2),
                actionButton: (entry) => onCardUnlisted && user ? (
                  <button
                    onClick={() => handleCancelSale(entry.id)}
                    disabled={cancellingSaleId === entry.id}
                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    {cancellingSaleId === entry.id ? '...' : t('cardsStatus.removeFromSale')}
                  </button>
                ) : null
              }))}
            </div>
            {renderPagination(cardsForSalePage, setCardsForSalePage, cardsForSale.length)}
          </>
        ) : (
          <p className="text-gray-400">{t('cardsStatus.emptyCardsForSale')}</p>
        )}
      </div>

      <div className="football-card glass-football p-6">
        <div className="flex items-center gap-3 mb-4">
          <BadgeCheck className="w-6 h-6 text-yellow-400" />
          <h2 className="text-2xl font-bold text-[#1e293b]">{t('cardsStatus.soldCards')}</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
        ) : soldCards.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedSold.map((item) => renderCard(item, {
                keyPrefix: 'sold',
                label: t('cardsStatus.soldLabel'),
                dateLabel: t('cardsStatus.saleDate'),
                dateValue: (entry) => formatDateValue(entry.soldAt),
                amountValue: (entry) => parseFloat(entry.price || 0).toFixed(2)
              }))}
            </div>
            {renderPagination(soldPage, setSoldPage, soldCards.length)}
          </>
        ) : (
          <p className="text-gray-400">{t('cardsStatus.emptySold')}</p>
        )}
      </div>

      <div className="football-card glass-football p-6">
        <div className="flex items-center gap-3 mb-4">
          <Repeat className="w-6 h-6 text-yellow-400" />
          <h2 className="text-2xl font-bold text-[#1e293b]">{t('cardsStatus.listedLoans')}</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
        ) : listedLoans.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedListedLoans.map((item) => renderCard(item, {
                keyPrefix: 'listedloan',
                label: t('cardsStatus.listedLoanLabel'),
                dateLabel: t('cardsStatus.listedDate'),
                dateValue: (entry) => formatDateValue(entry.listedAt),
                amountValue: listedLoanAmount,
                extraValue: listedLoanExtra,
                actionButton: renderListedLoanActions
              }))}
            </div>
            {renderPagination(listedLoansPage, setListedLoansPage, listedLoans.length)}
          </>
        ) : (
          <p className="text-gray-400">{t('cardsStatus.emptyListedLoans')}</p>
        )}
      </div>

      <div className="football-card glass-football p-6">
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-6 h-6 text-yellow-400" />
          <h2 className="text-2xl font-bold text-[#1e293b]">{t('cardsStatus.borrowedCards')}</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
        ) : borrowedCards.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedBorrowed.map((item) => renderCard(item, {
                keyPrefix: 'borrowed',
                label: t('cardsStatus.borrowedLabel'),
                dateLabel: t('cardsStatus.startDate'),
                dateValue: (entry) => formatDateValue(entry.startedAt),
                amountValue: (entry) => parseFloat(entry.totalPrice || 0).toFixed(2),
                extraValue: (entry) => t('cardsStatus.duration', { count: entry.durationGW })
              }))}
            </div>
            {renderPagination(borrowedPage, setBorrowedPage, borrowedCards.length)}
          </>
        ) : (
          <p className="text-gray-400">{t('cardsStatus.emptyBorrowed')}</p>
        )}
      </div>

      <div className="football-card glass-football p-6">
        <div className="flex items-center gap-3 mb-4">
          <ArrowLeftRight className="w-6 h-6 text-yellow-400" />
          <h2 className="text-2xl font-bold text-[#1e293b]">{t('cardsStatus.loanedCards')}</h2>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-400">{t('common.loading')}</div>
        ) : loanedCards.length > 0 ? (
          <>
            <div className="space-y-3">
              {paginatedLoaned.map((item) => renderCard(item, {
                keyPrefix: 'loaned',
                label: t('cardsStatus.loanedLabel'),
                dateLabel: t('cardsStatus.startDate'),
                dateValue: (entry) => formatDateValue(entry.startedAt),
                amountValue: (entry) =>
                  (entry.loanKind === 'period'
                    ? parseFloat(entry.pricePerPeriod || entry.totalPrice || 0)
                    : parseFloat(entry.totalPrice || 0)
                  ).toFixed(2),
                extraValue: loanedExtra
              }))}
            </div>
            {renderPagination(loanedPage, setLoanedPage, loanedCards.length)}
          </>
        ) : (
          <p className="text-gray-400">{t('cardsStatus.emptyLoaned')}</p>
        )}
      </div>
    </div>
  );
};

export default CardsStatusTab;
